"use client";

import React from "react";
import { DetailDrawer, DetailDrawerSkeleton } from "@/components/ui/detail-drawer";
import { Text } from "@/components/ui/text";
import { Separator } from "@/components/ui/separator";
import { SectionsService } from "@/api-client/sections";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { getSuggestionDisplayTitle } from "@/lib/suggestion-display-title";
import { Sparkles, Target } from "lucide-react";

interface SuggestionDetailDrawerProps {
  suggestionId: string | number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  iconType?: "target" | "sparkles";
}

export const SuggestionDetailDrawer: React.FC<SuggestionDetailDrawerProps> = ({
  suggestionId,
  open,
  onOpenChange,
  iconType = "sparkles",
}) => {
  const { data, isLoading, error } = useAuthenticatedQuery<{
    id: number;
    title: string;
    description: string;
    challenge?: string | null;
    response?: string | null;
    explanation: string;
    reasoning?: string | null;
  }>({
    queryKey: ["suggestionDetails", suggestionId],
    queryFn: () => {
      if (!suggestionId) throw new Error("Suggestion ID is required");
      return SectionsService.getSuggestionDetails(suggestionId);
    },
    enabled: open && suggestionId !== null,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const IconComponent = iconType === "target" ? Target : Sparkles;

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Suggestion Details"
      side="right"
    >
      {isLoading && <DetailDrawerSkeleton />}
      
      {error ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Text size="base" weight="medium" color="muted">
            Failed to load suggestion details. Please try again.
          </Text>
        </div>
      ) : null}

      {!isLoading && data ? (
        <>
          {/* Title Section with Icon */}
          <div className="space-y-3 pb-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 mt-1">
                <IconComponent
                  className="w-6 h-6"
                  strokeWidth={2}
                  stroke="var(--base-primary, #FF531B)"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Text as="h3" size="xl" weight="semibold" color="default" className="leading-tight">
                  {getSuggestionDisplayTitle(data.title)}
                </Text>
              </div>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Challenge / Response or Insight (description) */}
          {(data.challenge?.trim() || data.response?.trim()) ? (
            <div className="space-y-4 pb-4">
              {data.challenge?.trim() && (
                <div className="space-y-2">
                  <Text as="h4" size="base" weight="semibold" color="default">
                    Challenge
                  </Text>
                  <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                    {data.challenge}
                  </Text>
                </div>
              )}
              {data.response?.trim() && (
                <div className="space-y-2">
                  <Text as="h4" size="base" weight="semibold" color="default">
                    Response
                  </Text>
                  <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                    {data.response}
                  </Text>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 pb-4">
              <Text as="h4" size="base" weight="semibold" color="default">
                Insight
              </Text>
              <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                {data.description}
              </Text>
            </div>
          )}

          {/* Explanation Section */}
          {data.explanation && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3 pb-4">
                <Text as="h4" size="base" weight="semibold" color="default">
                Context
                </Text>
                <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                  {data.explanation}
                </Text>
              </div>
            </>
          )}

          {/* Rationale Section */}
          {data.reasoning && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3 pb-4">
                <Text as="h4" size="base" weight="semibold" color="default">
                  Rationale
                </Text>
                <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                  {data.reasoning}
                </Text>
              </div>
            </>
          )}
        </>
      ) : null}
    </DetailDrawer>
  );
};

